/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { computed, ref, useRouter, useContext, onMounted } from "@nuxtjs/composition-api";
import { debounce } from "lodash-es";
import cartGetters from "~/modules/checkout/getters/cartGetters";
import { useUiNotification, useExternalCheckout, useImage, useProduct, useUiState } from "~/composables";
import { useCart } from "~/modules/checkout/composables/useCart";
import { useUser } from "~/modules/customer/composables/useUser";
import { useVoucher } from "~/composables";
import type { UseCartViewInterface } from "~/modules/checkout/composables/useCartView/useCartView";
import type { ConfigurableCartItem, BundleCartItem, CartItemInterface } from "~/modules/GraphQL/types";
import { ProductStockStatus } from "~/modules/GraphQL/types";
import { ParamListVoucher, ParamVoucherInfo } from "~/composables/useVoucher/useVoucher";
import { useCustomerStore } from "~/modules/customer/stores/customer";
import useWishlist from "~/modules/wishlist/composables/useWishlist";
import CryptoJS from "crypto-js";
import api from "~/config/extensionsApi";

/**
 * Allows loading and manipulating cart view.
 *
 * See the {@link UseCartViewInterface} for a list of methods and values available in this composable.
 */
export function useCartView(): UseCartViewInterface {
  const {
    localePath,
    app: { i18n },
  } = useContext();
  const { initializeCheckout } = useExternalCheckout();
  const { getMagentoImage, imageSizes } = useImage();
  const router = useRouter();
  const { getProductPath } = useProduct();
  const { toggleLoginModal } = useUiState();
  const { addOrRemoveItem, isInWishlist } = useWishlist();
  const {
    cart,
    removeItem,
    updateItem,
    updateItemQty,
    load: loadCart,
    loading,
    loadingVoucherCart,
    applyMultiCoupon,
    listAppliedCoupon,
    appliedCoupons,
    loadFreeGiftList: loadFreeGift,
    freeGiftConfig,
    addCartFreeGift,
  } = useCart();
  const { isAuthenticated } = useUser();
  const customerStore = useCustomerStore();
  const user = customerStore.user;
  const { send: sendNotification, notifications } = useUiNotification();
  const {
    postListVoucher,
    loading: loadingVoucher,
    listVoucher,
    postVoucherInfo,
    listVoucherInfo,
  } = useVoucher();

  const products = computed(() =>
    cartGetters
      .getItems(cart.value)
      .filter(Boolean)
      .map((item) => ({
        ...item,
        product: {
          ...item.product,
          ...[(item as ConfigurableCartItem).configured_variant ?? {}],
          original_sku: item.product.sku,
        },
      }))
  );

  const totals = computed(() => cartGetters.getTotals(cart.value));
  const discount = computed(() => -cartGetters.getDiscountAmount(cart.value));
  const shippingPrice = computed(() => cartGetters.getShippingPrice(cart.value));
  const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
  const lengthItems = computed(() => cart.value?.items?.length || 0);
  const appliedCouponsOnCart = computed(() => {
    const couponCode = cart.value?.extension_attributes?.coupon_code_odoo;
    if (!couponCode) return [];
    return couponCode.split(",").map((code) => code.trim());
  });
  const getAvailableShippingMethod = computed(() => {
    return cartGetters.getSelectedShippingMethod(cart.value);
  });
  const freeShipPrice = computed(() => cartGetters.getFreeShippingPrice(cart.value));
  const freeGiftPrice = computed(() => cartGetters.getFreeGiftPrice(cart.value));

  const extensionAttributes = computed(() => {
    return cartGetters.getExtensionAttributes(cart.value);
  });

  const getAttributes = (product: ConfigurableCartItem) => product.configurable_options || [];
  const getBundles = (product: BundleCartItem) => product.bundle_options?.map((b) => b.values).flat() || [];
  const isRemoveModalVisible = ref(false);
  const itemToRemove = ref<CartItemInterface>();

  onMounted(() => {
    if (!cart.value?.id) {
      loadCart();
    }
  });

  const goToCheckout = async () => {
    await router.push("/checkout");
  };

  const showRemoveItemModal = ({ product }: { product: CartItemInterface }) => {
    if (notifications.value.length > 0) {
      notifications.value[0].dismiss();
    }

    isRemoveModalVisible.value = true;
    itemToRemove.value = product;
  };

  const removeItemAndSendNotification = async (product: CartItemInterface) => {
    try {
      await removeItem({ product });
    } catch (error) {
      sendNotification({
        id: Symbol('product_removed_error'),
        message: i18n.t('An error occurred while removing the product from your cart') as string,
        icon: 'error',
        persist: false,
        title: 'Error',
      });
    } finally {
      isRemoveModalVisible.value = false;

      sendNotification({
        id: Symbol('product_removed'),
        message: i18n.t('{0} has been successfully removed from your cart', {
          0: cartGetters.getItemName(
            product,
          ),
        }) as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'Product removed',
      });
    }
  };

  const getListVoucher = async () => {
    if (!user) {
      const param = {
        limit: 50,
        offset: 0,
        customerId: 0,
        phoneNumber: "",
      } as ParamListVoucher;
      await postListVoucher(param);
    } else {
      const { phone_number, customer_id } = user;

      if (!phone_number || !customer_id) {
        console.error("Customer ID or phone number is missing.");
        return;
      }

      let key = CryptoJS.enc.Hex.parse(api.otp.crypto_key);
      let iv = CryptoJS.enc.Hex.parse(api.otp.crypto_iv);
      let encrypted = CryptoJS.AES.encrypt(phone_number, key, { iv: iv });
      encrypted = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

      const param = {
        limit: 50,
        offset: 0,
        customerId: customer_id,
        phoneNumber: encrypted,
      } as ParamListVoucher;
      await postListVoucher(param);
    }
  };

  const getVoucherInfo = async (couponCodes: string[]) => {
    const param: ParamVoucherInfo = {
      coupon_code: couponCodes,
    };
    await postVoucherInfo(param);
  };

  const applyMultiCouponCard = async (couponCode: string) => {
    await applyMultiCoupon(couponCode);
  };

  const loadTotalCart = async () => {
    await loadCart();
  };

  const checkExitCart = () => {
    if (!cart.value?.items || cart.value.items.length === 0) {
      return "";
    }

    let hasOutOfStockError = false;
    let hasQuantityError = false;
    for (const item of cart.value.items) {
      const stockQty = item.dataInStock?.qty;

      if (stockQty === 0) {
        hasOutOfStockError = true;
        break;
      } else if (item.quantity > stockQty) {
        hasQuantityError = true;
      }
    }
    if (hasOutOfStockError) {
      return "Có sản phẩm hết hàng trong giỏ, vui lòng kiểm tra lại!";
    }

    if (hasQuantityError) {
      return "Có sản phẩm thay đổi số lượng trong giỏ, vui lòng kiểm tra lại!";
    }
    return "";
  };

  const updateItemCard = async (skuNew, uidRemove, quantity) => {
    try {
      await updateItem({ skuNew: skuNew, uidRemove: uidRemove, quantity: quantity });
    } catch (error) {
    } finally {
      sendNotification({
        id: Symbol('item_cart_updated'),
        message: i18n.t('Product edit successful: {0}', {
          0: skuNew,
        }) as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'ItemCard updated',
      });
    }
  };

  const loadFreeGifts = async (ruleId: string) => {
    await loadFreeGift(ruleId);
  };

  const isloadingFreeGift = ref(false);
  const addToCartFreeGift = async (param: any) => {
    try {
      isloadingFreeGift.value = true;
      await addCartFreeGift(param);
      await loadCart();
    } catch (error) {
      console.log("error: ", error);
    }
    finally {
      sendNotification({
        id: Symbol("free_gift"),
        type: "success",
        message: "Quà tặng đã được thêm vào giỏ hàng",
        icon: "check",
        persist: false,
        title: "free_gift",
      });
      isloadingFreeGift.value = false;
    }
  };

  const delayedUpdateItemQty = debounce((params) => updateItemQty(params), 1000);

  const isInStock = (product: CartItemInterface) => cartGetters.getStockStatus(product) === ProductStockStatus.InStock;

  const checkIsInWishlist = (cartProduct) => {
    try {
      if (!cartProduct) return false;
      return isInWishlist({ product: cartProduct });
    } catch (e) {
      console.error("Lỗi kiểm tra wishlist:", e);
      return false;
    }
  };

  const addItemToWishlist = async ({ product }) => {
    if (!isAuthenticated.value) {
      toggleLoginModal();
      return;
    }
    try {
      await addOrRemoveItem({ product });
      sendNotification({
        id: Symbol("add_to_wishlist"),
        message: "Thêm vào danh sách yêu thích thành công",
        type: "success",
        icon: "check",
        persist: false,
        title: "Thành công",
      });
    } catch (error) {
      console.error("Lỗi thêm vào danh sách yêu thích:", error);
    }
  };

  const numberOfFreeGift = computed(() => {
    let count = 0;
    cart.value?.items?.map((item) => {
      if (item?.prices?.price?.value === 0) {
        count += item.quantity;
      }
    });
    return count;
  });

  return {
    showRemoveItemModal,
    removeItemAndSendNotification,
    delayedUpdateItemQty,
    goToCheckout,
    getAttributes,
    getBundles,
    isInStock,
    getMagentoImage,
    getProductPath,
    loading,
    isAuthenticated,
    products,
    isRemoveModalVisible,
    itemToRemove,
    totals,
    totalItems,
    lengthItems,
    imageSizes,
    discount,
    cartGetters,
    updateItemCard,
    getListVoucher,
    loadingVoucher,
    listVoucher,
    applyMultiCouponCard,
    appliedCoupons,
    listAppliedCoupon,
    getVoucherInfo,
    listVoucherInfo,
    loadTotalCart,
    appliedCouponsOnCart,
    checkExitCart,
    addItemToWishlist,
    checkIsInWishlist,
    loadingVoucherCart,
    getAvailableShippingMethod,
    freeShipPrice,
    freeGiftPrice,
    extensionAttributes,
    loadFreeGifts,
    freeGiftConfig,
    addToCartFreeGift,
    numberOfFreeGift,
    isloadingFreeGift,
    shippingPrice,
  };
}

export default useCartView;
export * from "./useCartView";
